// VERSION 2.2 - Implementierung Spielmodi
// =============================================================================
//  KOMPONENTE: DuelScoreModal (Ergebniserfassung für KO-Duelle)
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

const DuelScoreModal = ({ duel, onClose, onSave, gameMode = 'SPRINT' }) => {
  // Annahme: duel = { playerA: scoreObjectA, playerB: scoreObjectB }
  const [scores, setScores] = useState(null);
  const [suddenVictory, setSuddenVictory] = useState({ a: '', b: '' });

  // Initialisiert den State, wenn das Modal geöffnet wird oder sich das Duell ändert
  useEffect(() => {
    if (duel) {
      // Erstelle eine tiefe Kopie, um die originalen Props nicht zu mutieren
      const initialScores = JSON.parse(JSON.stringify({
        playerA: duel.playerA,
        playerB: duel.playerB,
      }));
      setScores(initialScores);
      setSuddenVictory({
        a: initialScores.playerA.suddenVictoryThrow || '',
        b: initialScores.playerB.suddenVictoryThrow || ''
      });
    }
  }, [duel]);

  // Die Kernlogik: Berechnet bei jeder Änderung das Duellergebnis
  const duelResult = useMemo(() => {
    if (!scores) return null;

    let spA = 0, spB = 0;
    const totalA = scores.playerA.sets.reduce((sum, set) => sum + (Number(set.volle) || 0) + (Number(set.abr) || 0), 0);
    const totalB = scores.playerB.sets.reduce((sum, set) => sum + (Number(set.volle) || 0) + (Number(set.abr) || 0), 0);

    // Satzpunkte berechnen (Annahme: 2 Sätze für Sprint)
    for (let i = 0; i < scores.playerA.sets.length; i++) {
      const setA = scores.playerA.sets[i];
      const setB = scores.playerB.sets[i];
      const setTotalA = (Number(setA.volle) || 0) + (Number(setA.abr) || 0);
      const setTotalB = (Number(setB.volle) || 0) + (Number(setB.abr) || 0);

      if (setTotalA > setTotalB) {
        spA += 1;
      } else if (setTotalB > setTotalA) {
        spB += 1;
      } else if (setTotalA === setTotalB && setTotalA > 0) {
        spA += 0.5;
        spB += 0.5;
      }
    }

    let winner = null;
    let needsSuddenVictory = false;

    if (spA > spB) {
      winner = 'playerA';
    } else if (spB > spA) {
      winner = 'playerB';
    } else { // Satzpunkte sind gleich, Gesamtergebnis entscheidet
      if (totalA > totalB) {
        winner = 'playerA';
      } else if (totalB > totalA) {
        winner = 'playerB';
      } else { // Alles gleich, Sudden Victory wird benötigt
        needsSuddenVictory = true;
        const svA = Number(suddenVictory.a) || 0;
        const svB = Number(suddenVictory.b) || 0;
        if (svA > svB) {
          winner = 'playerA';
        } else if (svB > svA) {
          winner = 'playerB';
        }
      }
    }
    
    return { spA, spB, totalA, totalB, winner, needsSuddenVictory };
  }, [scores, suddenVictory]);

  const handleScoreChange = (player, setIndex, field, value) => {
    // Erlaube nur Zahlen und leere Strings
    if (!/^[0-9]*$/.test(value)) return;

    setScores(prev => {
      const newScores = { ...prev };
      newScores[player].sets[setIndex][field] = value;
      return newScores;
    });
  };

  const handleSuddenVictoryChange = (player, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    setSuddenVictory(prev => ({ ...prev, [player]: value }));
  };

  const handleSave = () => {
    if (!duelResult || !duelResult.winner) {
      return toast.warn("Das Duell ist noch nicht entschieden. Bitte alle Ergebnisse eintragen.");
    }

    // Bereite die Daten für das Backend vor
    const finalScoresA = {
      ...scores.playerA,
      setPoints: duelResult.spA,
      duelOutcome: duelResult.winner === 'playerA' ? 'win' : 'loss',
      suddenVictoryThrow: Number(suddenVictory.a) || 0
    };
    const finalScoresB = {
      ...scores.playerB,
      setPoints: duelResult.spB,
      duelOutcome: duelResult.winner === 'playerB' ? 'win' : 'loss',
      suddenVictoryThrow: Number(suddenVictory.b) || 0
    };
    
    onSave(finalScoresA, finalScoresB);
  };

  if (!scores) return null;

  const PlayerColumn = ({ playerKey, name }) => {
    const playerData = scores[playerKey];
    const sp = playerKey === 'playerA' ? duelResult?.spA : duelResult?.spB;

    return (
      <div className="duel-player-column">
        <h3>{name}</h3>
        <table className="score-modal-table">
          <thead>
            <tr><th>Satz</th><th>Volle</th><th>Abr.</th><th>Gesamt</th></tr>
          </thead>
          <tbody>
            {playerData.sets.map((set, i) => {
              const setTotal = (Number(set.volle) || 0) + (Number(set.abr) || 0);
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td><input type="number" value={set.volle} onChange={(e) => handleScoreChange(playerKey, i, 'volle', e.target.value)} /></td>
                  <td><input type="number" value={set.abr} onChange={(e) => handleScoreChange(playerKey, i, 'abr', e.target.value)} /></td>
                  <td>{setTotal}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="duel-summary">
          <span>Gesamtkegel: <strong>{playerKey === 'playerA' ? duelResult?.totalA : duelResult?.totalB}</strong></span>
          <span>Satzpunkte: <strong>{sp}</strong></span>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content" style={{ maxWidth: '900px' }}>
        <h2>Duell-Ergebniseingabe</h2>
        <div className="duel-container">
          <PlayerColumn playerKey="playerA" name={`${duel.playerA.entry.player.firstName} ${duel.playerA.entry.player.lastName}`} />
          <div className="duel-separator">vs.</div>
          <PlayerColumn playerKey="playerB" name={`${duel.playerB.entry.player.firstName} ${duel.playerB.entry.player.lastName}`} />
        </div>

        {duelResult?.needsSuddenVictory && (
          <div className="sudden-victory-section">
            <h4>Sudden Victory (Entscheidungswurf)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>{duel.playerA.entry.player.lastName}</label>
                <input type="number" value={suddenVictory.a} onChange={(e) => handleSuddenVictoryChange('a', e.target.value)} />
              </div>
              <div className="form-group">
                <label>{duel.playerB.entry.player.lastName}</label>
                <input type="number" value={suddenVictory.b} onChange={(e) => handleSuddenVictoryChange('b', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        <div className="duel-winner-info">
          {duelResult?.winner ? (
            <span>Sieger: <strong>{duelResult.winner === 'playerA' ? duel.playerA.entry.player.lastName : duel.playerB.entry.player.lastName}</strong></span>
          ) : (
            <span>Duell noch unentschieden...</span>
          )}
        </div>

        <div className="form-buttons">
          <button type="button" onClick={handleSave} className="btn-primary-action">Duell-Ergebnis speichern</button>
          <button type="button" className="btn-cancel" onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

export default DuelScoreModal;